import { Fragment, useContext, useEffect, useCallback, useMemo, useRef } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import { Heading4Style, Heading5Style } from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import LineArrowUpRightIcon from 'LuxKit/Icons/line/LineArrowUpRightIcon'
import LineFolderDownloadIcon from 'LuxKit/Icons/line/LineFolderDownloadIcon'
import Group from 'components/utils/Group'
import ContactContext from 'contexts/contactContext'
import DevAssistantRawBlock from './DevAssistantRawBlock'
import AddedBookingTiles, {
  extractAddedBookingTilesFromMessage,
} from './AddedBookingTiles'
import MarkdownRender from 'LuxKit/MarkdownRender'
import MessageContextLabel from './MessageContextLabel'
import ChatHotelCarousel, { type CarouselOccupancy } from './ChatHotelCarousel'
import ChatFlightCarousel from './ChatFlightCarousel'
import { toFlightUIData } from './chatFlightTypes'
import type { CardOption, CardCategory } from './CardsList'
import CardsList from './CardsList'
import ImageCardsList from './ImageCardsList'
import PillsList from './PillsList'
import { UI_COMPONENT_TYPES } from './constants'
import type { ContractMessagePart, ContractUIComponentData, StreamedProgressiveUiComponentData } from 'tripPlanner/api/types'
import { stripContextTags } from 'tripPlanner/utils/contextTag'
import { CLASSIC_TRIP_MIGRATION_PROMPT_PREFIX } from 'tripPlanner/utils/classicTripMigration'
import { useMessageActions, type OfferClickSearchParams } from 'tripPlanner/pages/chat/ChatTripPlannerPage/contexts/ChatMessageActionsContext'
import { isRecord } from 'lib/object/objectUtils'
import {
  isStreamedCardsListUiComponentData,
  isStreamedPillsListUiComponentData,
  isStreamedTestimonialUiComponentData,
} from 'tripPlanner/utils/streamedProgressiveUi'
import ChatEmbeddedAccountAccess from './EmbeddedAccountAccess'
import ExperienceVoucherWidget from './ExperienceVoucherWidget'
import ChatEmbeddedCustomFlight from './EmbeddedCustomFlight'
import Testimonial from './Testimonial'

type ExperienceVoucherUIComponentData = {
  type: typeof UI_COMPONENT_TYPES.EXPERIENCE_VOUCHER
  orderId: string
  experienceItemIds?: Array<string>
  primaryExperienceItemId?: string
  experienceOfferId?: string
  groupType?: 'individual_item' | 'shared_provider_booking' | 'uploaded_all_items'
  downloadAllOfferItemsVoucher?: boolean
  experienceItemId?: string
}

export type UIComponentData = ContractUIComponentData | ExperienceVoucherUIComponentData | StreamedProgressiveUiComponentData

export type MessagePart =
  | Exclude<ContractMessagePart, { type: 'ui_component' }>
  | { type: 'ui_component'; data: UIComponentData }

function getCardsListCategories(data: unknown): CardCategory | undefined {
  if (!isRecord(data)) return undefined
  const { categories } = data
  if (categories === 'generic' || categories === 'places' || categories === 'locations') {
    return categories
  }
  return undefined
}

type ExperienceVoucherGroup = {
  type: 'experience_voucher_group'
  vouchers: Array<{
    orderId: string
    experienceItemIds: Array<string>
    primaryExperienceItemId: string
    experienceOfferId?: string
    groupType?: 'individual_item' | 'shared_provider_booking' | 'uploaded_all_items'
    downloadAllOfferItemsVoucher?: boolean
  }>
}

type RenderItem = MessagePart | ExperienceVoucherGroup

function groupExperienceVouchers(parts: Array<MessagePart>): Array<RenderItem> {
  const result: Array<RenderItem> = []
  for (const part of parts) {
    if (part.type === 'ui_component' && isExperienceVoucherData(part.data)) {
      const last = result[result.length - 1]
      const voucherEntry = normalizeExperienceVoucherData(part.data)
      if (!voucherEntry) continue
      if (last?.type === 'experience_voucher_group') {
        last.vouchers.push(voucherEntry)
      } else {
        result.push({ type: 'experience_voucher_group', vouchers: [voucherEntry] })
      }
      continue
    }
    result.push(part)
  }
  return result
}

function normalizeExperienceVoucherData(data: ExperienceVoucherUIComponentData): ExperienceVoucherGroup['vouchers'][number] | null {
  const experienceItemIds = Array.isArray(data.experienceItemIds)
    ? data.experienceItemIds.filter((id) => typeof id === 'string' && id.length > 0)
    : []
  const primaryExperienceItemId = data.primaryExperienceItemId ?? data.experienceItemId ?? experienceItemIds[0]

  if (!primaryExperienceItemId) return null

  const groupedExperienceItemIds = experienceItemIds.includes(primaryExperienceItemId)
    ? experienceItemIds
    : [primaryExperienceItemId, ...experienceItemIds]

  return {
    orderId: data.orderId,
    experienceItemIds: groupedExperienceItemIds,
    primaryExperienceItemId,
    ...(data.experienceOfferId ? { experienceOfferId: data.experienceOfferId } : {}),
    ...(data.groupType ? { groupType: data.groupType } : {}),
    ...(data.downloadAllOfferItemsVoucher ? { downloadAllOfferItemsVoucher: true } : {}),
  }
}

type UrlActionLinkData = {
  type: 'action_link'
  action: string
  label: string
}

function isSafeUrlAction(action: string): boolean {
  if (!/^(\/(?!\/)|https?:\/\/)/i.test(action)) {
    return false
  }

  try {
    const parsedUrl = new URL(action, 'https://luxuryescapes.com')
    return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:'
  } catch {
    return false
  }
}

function isUrlActionLinkData(data: unknown): data is UrlActionLinkData {
  if (!isRecord(data)) return false
  return (
    data.type === 'action_link' &&
    typeof data.action === 'string' &&
    typeof data.label === 'string' &&
    isSafeUrlAction(data.action)
  )
}

function isExperienceVoucherData(data: unknown): data is ExperienceVoucherUIComponentData {
  if (!isRecord(data)) return false

  const { primaryExperienceItemId, experienceItemId, experienceItemIds } = data

  return (
    data.type === UI_COMPONENT_TYPES.EXPERIENCE_VOUCHER &&
    typeof data.orderId === 'string' &&
    (
      typeof primaryExperienceItemId === 'string' ||
      typeof experienceItemId === 'string' ||
      (Array.isArray(experienceItemIds) && experienceItemIds.some((id) => typeof id === 'string'))
    )
  )
}

function escapeRegex(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function parseChoiceSelections(userText: string, choiceLabels: Array<string>): Array<string> {
  const selections: Array<string> = []
  for (const label of choiceLabels) {
    const pattern = new RegExp(`(^|\\s|,|\\n)${escapeRegex(label)}(\\s|,|\\n|$)`, 'i')
    if (pattern.test(userText)) {
      selections.push(label)
    }
  }
  return selections
}

function stripChoiceSelections(text: string, choiceLabels: Array<string> | undefined, isDevMode: boolean): string {
  if (isDevMode || !choiceLabels || choiceLabels.length === 0) {
    return text
  }
  let working = text
  for (const label of choiceLabels) {
    const pattern = new RegExp(`(^|\\s|,)${escapeRegex(label)}(?=\\s|,|$)`, 'gi')
    working = working.replace(pattern, '$1')
  }
  const segments = working
    .split(',')
    .map(seg => seg.trim())
    .filter(Boolean)
  return segments.join(', ').trim()
}

const ThumbnailsWrapper = styled.div`
  display: flex;
  gap: ${rem(8)};
  padding: 0 0 ${rem(8)} 0;
  overflow-x: auto;
  flex-wrap: wrap;
`

const Thumbnail = styled.div`
  position: relative;
  width: ${rem(48)};
  height: ${rem(48)};
  border-radius: ${rem(4)};
  background-color: ${props => props.theme.palette.neutral.default.six};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const FileIcon = styled.div`
  font-size: ${rem(10)};
  text-align: center;
  word-break: break-all;
  padding: ${rem(2)};
  color: ${props => props.theme.palette.neutral.default.two};
`

function FileThumbnail({ file }: { file: File }) {
  const intl = useIntl()
  const imageUrl = useMemo(() => {
    if (file.type.startsWith('image/')) {
      return URL.createObjectURL(file)
    }
    return null
  }, [file])

  useEffect(() => {
    return () => {
      if (imageUrl) URL.revokeObjectURL(imageUrl)
    }
  }, [imageUrl])

  return (
    <Thumbnail>
      {imageUrl
        ? <img src={imageUrl} alt={file.name} />
        : <FileIcon>{file.name.split('.').pop()?.toUpperCase() || intl.formatMessage({ id: 'eHfEIdFmGpPFUuFWaYEx', defaultMessage: 'File', description: 'Fallback file type label shown in the trip planner chat attachment thumbnail when no file extension is available' }).toUpperCase()}</FileIcon>}
    </Thumbnail>
  )
}

const MessageBubble = styled.div`
  width: 100%;
  padding: 0;
  border-radius: 0;
  background-color: transparent;
  color: ${props => props.theme.palette.neutral.default.one};

  &.user {
    width: auto;
    max-width: 75%;
    padding: ${rem(8)} ${rem(18)};
    border-radius: ${rem(18)};
    background-color: ${props => props.theme.palette.neutral.default.seven};
    color: ${props => props.theme.palette.neutral.default.two};
  }
`

const StyledBodyText = styled(BodyText)`
  overflow-wrap: break-word;
  white-space: pre-wrap;
  margin: 0;
`

const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${rem(4)};
  width: 100%;
`

const BubbleContent = styled.div`
  width: 100%;

  > * + * {
    margin-top: ${rem(20)};
  }

  > .ui-component {
    margin-top: ${rem(8)};
  }

  > .assistant-step-text + .assistant-step-text {
    margin-top: ${rem(12)};
  }

`

const ChatMarkdownWrapper = styled.div`
  h2 {
    ${Heading4Style}
  }
  h3 {
    ${Heading5Style}
  }

`

export type LocationInfoCategory = 'locations' | 'places'

export type LocationInfoData = {
  title: string
  description?: string
  emoji?: string
  placeSearch: string
  category: LocationInfoCategory
}

type ChatMessageProps = {
  messageId?: string
  role: 'user' | 'assistant'
  parts: Array<MessagePart>
  isMarkdown?: boolean
  files?: Array<File>
  rawContent?: string
  oaiConversationId?: string
  isLastMessage?: boolean
  multiChoiceInitialSelection?: Array<string>
  /** Per-message selection: live for last message, historical for older messages */
  externalSelection?: Array<string>
  previousChoiceOptions?: Array<string>
}

function Message({
  messageId,
  parts,
  role,
  isMarkdown = false,
  files,
  rawContent,
  oaiConversationId,
  isLastMessage = false,
  multiChoiceInitialSelection,
  externalSelection,
  previousChoiceOptions,
}: ChatMessageProps) {
  const intl = useIntl()
  const contactContext = useContext(ContactContext)
  const {
    onMultiChoiceSelectionChange,
    onOfferClick,
    onOfferHover,
    onHotelMapViewClick,
    onExperienceMapViewClick,
    onPlaceClick,
    onLocationInfoClick,
    onScheduleCallback,
    onSendMessage,
    onAuthenticatedFollowUp,
    itineraryData,
    isDevMode,
  } = useMessageActions()

  const handleExperienceOfferClick = useCallback(
    ({ offerId, searchParams }: { offerId: string; searchParams?: OfferClickSearchParams; offerType?: string }) => onOfferClick({ offerId, searchParams, offerType: 'experience' }),
    [onOfferClick],
  )

  const safeParts = useMemo(() => parts ?? [], [parts])
  const hasParts = safeParts.length > 0

  const isUser = role === 'user'
  const shouldRenderMarkdown = role === 'assistant' && !isDevMode
  const userMessageText = useMemo(() => {
    if (!isUser) {
      return undefined
    }

    if (rawContent) {
      return rawContent
    }

    return safeParts.find(
      (part): part is Extract<MessagePart, { type: 'text' }> => part.type === 'text',
    )?.content
  }, [isUser, rawContent, safeParts])
  const addedBookingTiles = useMemo(
    () => extractAddedBookingTilesFromMessage(userMessageText),
    [userMessageText],
  )

  const markdownContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const container = markdownContainerRef.current
    if (!container) return
    const cleanups: Array<() => void> = []

    // Place tags (Google Places locations)
    if (onPlaceClick) {
      container.querySelectorAll('.place-tag').forEach(tag => {
        const handler = (e: Event) => {
          const target = e.currentTarget as HTMLElement
          const placeId = target.dataset.placeId
          const placeName = target.textContent || target.dataset.placeName || ''
          if (placeId) {
            onPlaceClick({ googlePlaceId: placeId, placeName })
          }
        }
        tag.addEventListener('click', handler)
        cleanups.push(() => tag.removeEventListener('click', handler))
      })
    }

    // Offer tags (LE hotels and experiences)
    if (onOfferClick) {
      container.querySelectorAll('.offer-tag').forEach(tag => {
        const handler = (e: Event) => {
          const target = e.currentTarget as HTMLElement
          const offerId = target.dataset.offerId
          const offerType = target.dataset.offerType
          if (offerId) {
            onOfferClick({ offerId, offerType })
          }
        }
        tag.addEventListener('click', handler)
        cleanups.push(() => tag.removeEventListener('click', handler))
      })
    }

    return () => cleanups.forEach(fn => fn())
  }, [onPlaceClick, onOfferClick, safeParts, messageId])

  const handleMultiChoiceSelectionChange = useCallback((labels: Array<string>) => {
    onMultiChoiceSelectionChange(labels)
  }, [onMultiChoiceSelectionChange])

  const handleCardAction = useCallback((action: string) => {
    if (action === 'schedule_callback') {
      onScheduleCallback()
      return true
    }

    return false
  }, [onScheduleCallback])

  const handleLocationInfoClick = useCallback((option: CardOption, category: CardCategory) => {
    if (category !== 'locations' && category !== 'places') return
    const placeSearch = option.placeSearch?.trim()
    if (!placeSearch) return
    onLocationInfoClick({
      title: option.title,
      description: option.description,
      emoji: option.emoji,
      placeSearch,
      category,
    })
  }, [onLocationInfoClick])

  const renderPart = (part: MessagePart, index: number) => {
    if (part.type === 'trip_plan_content') {
      if (!isDevMode) {
        return null
      }
      return (
        <StyledBodyText
          key={index}
          variant="medium"
          colour="neutral-one"
        >
          {part.content}
        </StyledBodyText>
      )
    }

    if (part.type === 'ui_component') {
      // In dev mode, render as text instead of component
      if (isDevMode) {
        const jsonText = JSON.stringify(part.data, null, 2)
        return (
          <StyledBodyText
            key={index}
            variant="medium"
            colour="neutral-one"
          >
            {jsonText}
          </StyledBodyText>
        )
      }

      if (part.data.type === 'pills_list') {
        const isStreamingPillsList = isStreamedPillsListUiComponentData(part.data) && part.data.streamingStatus !== 'complete'
        const isLockedHistoryPillsList = !isLastMessage
        const pillSelection = isLockedHistoryPillsList ? multiChoiceInitialSelection : externalSelection
        return (
          <div key={index} className="ui-component">
            <PillsList
              options={part.data.options}
              select={part.data.select}
              onSelectionChange={handleMultiChoiceSelectionChange}
              disabled={isLockedHistoryPillsList}
              selection={pillSelection}
              isStreaming={isStreamingPillsList}
            />
          </div>
        )
      }

      if (part.data.type === 'cards_list') {
        const cardsData = part.data
        const categories = getCardsListCategories(cardsData)
        const isStreamingCardsList = isStreamedCardsListUiComponentData(cardsData) && cardsData.streamingStatus !== 'complete'

        const isLockedHistoryCardList = !isLastMessage
        const cardSelection = isLockedHistoryCardList ? multiChoiceInitialSelection : externalSelection

        if (categories === 'locations' || categories === 'places') {
          return (
            <div key={index} className="ui-component">
              <ImageCardsList
                options={cardsData.options}
                select={cardsData.select}
                onSelectionChange={handleMultiChoiceSelectionChange}
                disabled={isLockedHistoryCardList}
                selection={cardSelection}
                categories={categories}
                onInfoClick={handleLocationInfoClick}
                isStreaming={isStreamingCardsList}
              />
            </div>
          )
        }

        return (
          <div key={index} className="ui-component">
            <CardsList
              options={cardsData.options}
              select={cardsData.select}
              onSelectionChange={handleMultiChoiceSelectionChange}
              onAction={handleCardAction}
              disabled={isLockedHistoryCardList}
              selection={cardSelection}
              categories={categories}
              onInfoClick={handleLocationInfoClick}
              isStreaming={isStreamingCardsList}
            />
          </div>
        )
      }

      if (part.data.type === UI_COMPONENT_TYPES.CAROUSEL) {
        const carouselData = part.data as typeof part.data & {
          occupancy?: CarouselOccupancy
        }

        if (carouselData.product === 'hotel') {
          return (
            <ChatHotelCarousel
              key={index}
              offerIds={carouselData.offerIds}
              searchQuery={carouselData.title}
              occupancy={carouselData.occupancy}
              onOfferClick={onOfferClick}
              onOfferHover={onOfferHover}
              onMapViewClick={onHotelMapViewClick}
              itineraryData={itineraryData}
              onSendMessage={onSendMessage}
              trackingKey={`${messageId ?? 'message'}-${index}-${carouselData.product}`}
            />
          )
        }

        if (carouselData.product === 'cruise') {
          return (
            <ChatHotelCarousel
              key={index}
              offerIds={carouselData.offerIds}
              searchQuery={carouselData.title}
              cardType="cruise"
              onOfferClick={onOfferClick}
              trackingKey={`${messageId ?? 'message'}-${index}-${carouselData.product}`}
            />
          )
        }

        return (
          <ChatHotelCarousel
            key={index}
            offerIds={carouselData.offerIds}
            searchQuery={carouselData.title}
            cardType="experience"
            onOfferClick={handleExperienceOfferClick}
            onOfferHover={onOfferHover}
            onMapViewClick={onExperienceMapViewClick}
            itineraryData={itineraryData}
            onSendMessage={onSendMessage}
            trackingKey={`${messageId ?? 'message'}-${index}-${carouselData.product}`}
          />
        )
      }

      if (part.data.type === 'flight') {
        return (
          <div key={index} className="ui-component">
            <ChatFlightCarousel data={toFlightUIData(part.data)} onSendMessage={onSendMessage} />
          </div>
        )
      }

      if ((part.data as { type?: string }).type === UI_COMPONENT_TYPES.SIGN_UP_MODAL) {
        return (
          <ChatEmbeddedAccountAccess
            key={index}
            isLastMessage={isLastMessage}
            onAuthenticated={onAuthenticatedFollowUp}
          />
        )
      }

      if (part.data.type === UI_COMPONENT_TYPES.CUSTOM_FLIGHT) {
        return (
          <ChatEmbeddedCustomFlight
            key={index}
            isLastMessage={isLastMessage}
          />
        )
      }

      if (part.data.type === UI_COMPONENT_TYPES.TESTIMONIAL) {
        const isStreamingTestimonial = isStreamedTestimonialUiComponentData(part.data) && part.data.streamingStatus !== 'complete'
        return (
          <div key={index} className="ui-component">
            <Testimonial reviews={part.data.reviews} isStreaming={isStreamingTestimonial} />
          </div>
        )
      }

      if (part.data.type === 'action_link') {
        const { action, label } = part.data

        if (isUrlActionLinkData(part.data)) {
          return (
            <TextButton
              key={index}
              kind="secondary"
              size="medium"
              href={part.data.action}
              download
              endIcon={<LineFolderDownloadIcon />}
            >
              {part.data.label}
            </TextButton>
          )
        }

        if (action === 'call_expert') {
          const defaultContact = contactContext?.defaultContact
          if (!defaultContact) {
            return null
          }
          const contactText = label
            ? intl.formatMessage(
                {
                  id: 'ow1gwGY-0_8lHK5yKaVN',
                  defaultMessage: '{label} - {contact}',
                  description: 'Contact label paired with phone number for the call expert action in trip planner chat',
                },
                { label, contact: defaultContact.humanReadable },
              )
            : intl.formatMessage(
                {
                  id: 'KoIDLbooo4tckfqqz5AJ',
                  defaultMessage: 'Call {contact}',
                  description: 'Call-to-action button text with phone number for contacting a travel expert in trip planner chat',
                },
                { contact: defaultContact.humanReadable },
              )
          return (
            <TextButton
              key={index}
              kind="secondary"
              size="medium"
              href={`tel:${defaultContact.number}`}
              endIcon={<LineArrowUpRightIcon />}
            >
              {contactText}
            </TextButton>
          )
        }

        if (action === 'schedule_callback') {
          return (
            <TextButton
              key={index}
              kind="secondary"
              size="medium"
              onClick={onScheduleCallback}
              endIcon={<LineArrowUpRightIcon />}
            >
              {label}
            </TextButton>
          )
        }

        return null
      }

      return null
    }

    let text = part.content
    if (!text || text.trim() === '') {
      return null
    }

    if (isUser && text.trimStart().startsWith(CLASSIC_TRIP_MIGRATION_PROMPT_PREFIX)) {
      text = intl.formatMessage({
        id: '_cqRLglMEfwdhZlnwXFH',
        defaultMessage: 'Help move my trip over to Luxury Escapes’ new AI chat. This may take a minute or two.',
        description: 'Friendly message shown in the chat bubble when a classic trip migration prompt is submitted',
      })
    }

    // Strip choice labels from user bubble in non-dev mode (still visible in dev via raw content)
    if (isUser) {
      text = stripChoiceSelections(text, previousChoiceOptions, isDevMode)
    }

    // For user messages, strip <Context>...</Context> tags in non-dev mode
    // These tags contain highlighted text context sent to the LLM
    if (isUser && !isDevMode) {
      text = stripContextTags(text)
      if (!text) {
        return null
      }
    }

    // Backward compat: old messages may have raw hotel data (not Context-wrapped).
    // New messages wrap hotel data in <Context> tags and are stripped above.
    if (isUser) {
      const hotelInfoMatch = text.match(/^(.+?)(\n\n\d+\.\s.+)$/s)
      if (hotelInfoMatch) {
        const [, userMessage, hotelInfo] = hotelInfoMatch
        return (
          <Fragment key={index}>
            <StyledBodyText
              variant="medium"
              colour={isUser ? undefined : 'neutral-one'}
            >
              {userMessage}
            </StyledBodyText>
            {isDevMode && (
              <StyledBodyText
                variant="medium"
                colour="neutral-one"
                style={{ marginTop: rem(8), opacity: 0.7, fontSize: rem(12) }}
              >
                {hotelInfo}
              </StyledBodyText>
            )}
          </Fragment>
        )
      }
    }

    if (shouldRenderMarkdown) {
      return (
        <ChatMarkdownWrapper
          key={index}
          className={clsx(
            role === 'assistant' && 'assistant-step-text',
          )}
        >
          <MarkdownRender
            content={text}
            fontSize="medium"
            type="compact"
          />
        </ChatMarkdownWrapper>
      )
    }

    return (
      <StyledBodyText
        key={index}
        className={clsx(
          role === 'assistant' && 'assistant-step-text',
        )}
        variant="medium"
        colour={isUser ? undefined : 'neutral-one'}
      >
        {text}
      </StyledBodyText>
    )
  }

  const renderItem = (item: RenderItem, index: number) => {
    if (item.type === 'experience_voucher_group') {
      if (isDevMode) {
        return (
          <StyledBodyText
            key={index}
            variant="medium"
            colour="neutral-one"
          >
            {JSON.stringify(item.vouchers, null, 2)}
          </StyledBodyText>
        )
      }
      return (
        <div key={index} className="ui-component">
          <ExperienceVoucherWidget vouchers={item.vouchers} />
        </div>
      )
    }
    return renderPart(item, index)
  }

  const renderItems = useMemo(() => groupExperienceVouchers(safeParts), [safeParts])
  const messageContent = renderItems.map(renderItem).filter(Boolean)
  const devRawContent = isDevMode && role === 'assistant' && rawContent
    ? <DevAssistantRawBlock rawContent={rawContent} oaiConversationId={oaiConversationId} />
    : null
  const shouldRenderMessageContent = !(isDevMode && role === 'assistant')
  const hasBubbleContent = (shouldRenderMessageContent && messageContent.length > 0) ||
    Boolean(files && files.length > 0) ||
    Boolean(devRawContent)
  const hasRenderableContent = hasBubbleContent ||
    addedBookingTiles.length > 0
  const choiceSelections = useMemo(() => {
    if (!isUser || !previousChoiceOptions || previousChoiceOptions.length === 0) {
      return []
    }
    const sourceText = rawContent || safeParts.find(p => p.type === 'text')?.content || ''
    if (!sourceText) {
      return []
    }
    return parseChoiceSelections(stripContextTags(sourceText), previousChoiceOptions)
  }, [isUser, previousChoiceOptions, rawContent, safeParts])

  if (!hasParts) {
    return null
  }

  if (isMarkdown && !isDevMode) {
    return null
  }

  if (!isDevMode && !hasRenderableContent) {
    // Allow rendering context label even if the bubble is empty (e.g., choice-only submissions).
    if (!(isUser && (previousChoiceOptions?.length ?? 0) > 0)) {
      return null
    }
  }

  // Check if we should show context label (only for user messages)
  const showContextLabel = isUser

  const bubbleContent = (
    <BubbleContent ref={markdownContainerRef}>
      {files && files.length > 0 && (
        <ThumbnailsWrapper>
          {files.map((file, index) => (
            <FileThumbnail key={`${file.name}-${index}`} file={file} />
          ))}
        </ThumbnailsWrapper>
      )}
      {shouldRenderMessageContent && messageContent}
      {devRawContent}
    </BubbleContent>
  )

  const bubbleNode = hasBubbleContent
    ? <MessageBubble className={clsx({ user: isUser })}>{bubbleContent}</MessageBubble>
    : null
  const addedBookingTilesNode = addedBookingTiles.length > 0
    ? <AddedBookingTiles tiles={addedBookingTiles} />
    : null

  const messageWithContext = (
    <MessageWrapper>
      <MessageContextLabel parts={parts} selectionLabels={choiceSelections} />
      {bubbleNode}
      {addedBookingTilesNode}
    </MessageWrapper>
  )

  const messageWithoutContext = bubbleNode

  return (
    <Group
      direction="horizontal"
      horizontalAlign={isUser ? 'end' : 'start'}
      fullWidth
      data-message-id={messageId}
    >
      {showContextLabel ? messageWithContext : messageWithoutContext}
    </Group>
  )
}

export default Message
